import {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  AddressDTO,
  GetApiPatientGetVitalsHistoryPatientIdParams,
  GetApiSettingsGePatientHistoryOptionsParams,
  PatientMediaCategoryDetailsResponseDTO,
  PatientMediaCategoryDTO,
  PatientMediaSearchResultDTOPaginatedResponseDTO,
  StringPaginatedResponseDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import {
  getApiAddressGetGeoCodeByAddress,
  getApiPatientGetVitalsHistoryPatientId,
  getApiMultimediaGetPatientMediaCategoryDetails,
  getApiMultimediaSearchPatientMultimediaPatientId,
  getApiSettingsGePatientHistoryOptions,
} from 'endpoints';
import values from 'lodash/values';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import omit from 'lodash/omit';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import {
  MultimediaTypeWithCategory,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog';
import compact from 'lodash/compact';
import {
  Place,
} from 'pages/Dashboard/components/AddressSuggestion';
import {
  VitalsHistoryItem,
} from 'core/vitals/types';
import {
  getVitalsHistoryDate,
} from 'core/vitals/utils';
import dayjs from 'utils/dayjs';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  DateRange,
} from 'types/dateRange';

function serializeVitalsHistory(dateRange: DateRange): GetApiPatientGetVitalsHistoryPatientIdParams {
  const serializedDateRange = serializeDateRange(dateRange);
  return {
    start: serializedDateRange?.start,
    end: serializedDateRange?.end,
  };
}

export function useVitalsHistory(patientId: number, dateRange: DateRange) {
  const params = serializeVitalsHistory(dateRange);

  return useQuery<VitalsHistoryItem[], Error>({
    queryKey: ['patient-vitals-history', patientId, params.start, params.end],
    queryFn: async () => {
      const items = await getApiPatientGetVitalsHistoryPatientId(patientId, params);

      return [...items].sort((left, right) => (
        dayjs(getVitalsHistoryDate(left)).valueOf() - dayjs(getVitalsHistoryDate(right)).valueOf()
      ));
    },
    enabled: patientId > 0,
  });
}

export function useGoogleMapGeoCode(address: string): UseQueryResult<AddressDTO, string> {
  return useQuery<AddressDTO, string>({
    queryKey: ['postalCode', address],
    queryFn: () => getApiAddressGetGeoCodeByAddress({ address }),
    enabled: !isEmptyString(address),
  });
}

export const useGetGooglePlaces = (
  input: string,
): UseQueryResult<Partial<Place>[], Error> => {
  const autocompleteSuggestionRef = useRef<
    typeof google.maps.places.AutocompleteSuggestion | null
  >(null);

  // Cache it once before creating the query. (AutocompleteSuggestion is static-only.)
  useEffect(() => {
    if (isNil(autocompleteSuggestionRef.current)
      && !isNil(window?.google?.maps?.places?.AutocompleteSuggestion)
    ) {
      autocompleteSuggestionRef.current = window.google.maps.places.AutocompleteSuggestion;
    }
  }, []);

  return useQuery<Partial<google.maps.places.AutocompletePrediction>[], Error>({
    queryKey: ['places', input],
    queryFn: async () => {
      const AutocompleteSuggestion = autocompleteSuggestionRef.current;
      if (isNil(AutocompleteSuggestion)) {
        console.error('Google Places library not loaded');
        throw new Error('Google Places library not loaded');
      }

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['us'],
      });

      return compact((suggestions ?? [])
        .map((suggestion) => {
          const prediction = suggestion.placePrediction;
          const matches = prediction?.mainText?.matches ?? [];

          // Normalize new API result into legacy AutocompletePrediction-like
          // shape used by Address forms and `formatPlace()`.
          return isNil(prediction) ? null : {
            description: prediction.text?.text ?? '',
            place_id: prediction.placeId,
            structured_formatting: {
              main_text: prediction.mainText?.text ?? '',
              secondary_text: prediction.secondaryText?.text ?? '',
              main_text_matched_substrings: matches.map((m) => ({
                offset: m.startOffset,
                length: m.endOffset - m.startOffset,
              })),
            },
          } as Partial<Place>;
        }));
    },
    enabled: !isEmptyString(input),
  });
};

export function useSearchPatientHistoryOptions(params: GetApiSettingsGePatientHistoryOptionsParams):
  UseQueryResult<StringPaginatedResponseDTO, Error> {
  return usePrefetchedData<StringPaginatedResponseDTO>({
    key: ['patient-History-options', ...values(omit(params, 'pageSize')) as string[]],
    fetchFn: () => getApiSettingsGePatientHistoryOptions(params),
  });
}

export function useGetMediaCategories(): PatientMediaCategoryDTO[] {
  const { data } = usePrefetchedData<PatientMediaCategoryDetailsResponseDTO>({
    key: ['patient-media-categories'],
    fetchFn: getApiMultimediaGetPatientMediaCategoryDetails,
  });

  return (data?.categories ?? []) as PatientMediaCategoryDTO[];
}

export function useGetMediaTypesList(uploadableOnly = true): MultimediaTypeWithCategory[] {
  const categories = useGetMediaCategories();

  return useMemo(() => {
    const mediaTypes = flatMap(categories, (category) =>
      map(category?.mediaTypes, (value) => ({
        ...value,
        category: category?.categoryName ?? '',
      })));

    return mediaTypes.filter((mediaType) => !uploadableOnly || mediaType.isUploadableFromChart);
  }, [categories, uploadableOnly]);
}

export function useGetMediaCategoryGroups(): string[] {
  const categories = useGetMediaCategories();

  return useMemo(() => compact(map(categories, 'categoryName')), [categories]);
}

export function useGetMediaCategoryTabOptions(): string[] {
  const categoryGroupNames = useGetMediaCategoryGroups();

  return useMemo(() => ['All', ...categoryGroupNames], [categoryGroupNames]);
}

export function useSearchPatientMultimedia(
  patientId: number,
  searchText: string,
  page: number,
  pageSize: number,
) {
  return useQuery<PatientMediaSearchResultDTOPaginatedResponseDTO, Error>({
    queryKey: ['patient-multimedia-search', patientId, searchText],
    queryFn: () => getApiMultimediaSearchPatientMultimediaPatientId(
      patientId,
      { searchText, page, pageSize },
    ),
    enabled: !isEmptyString(searchText),
  });
}
